import { useState, type FC, type FormEvent } from "react";
import { addCertificatePolicy } from "../stores/certificate_policy_store";
import {
  AUTHENTICATION_TYPES,
  KEY_TYPES,
  HASH_TYPES,
  KEY_LENGTHS_BY_TYPE,
  getSignatureAlgorithm,
  type AuthenticationType,
  type KeyType,
  type HashType,
  type CertificatePolicy,
} from "../../../shared/models/certificate_policy";

export const CreatePolicyModal: FC<{
  duplicateFrom?: CertificatePolicy;
  onClose: () => void;
}> = ({ duplicateFrom, onClose }) => {
  const [name, setName] = useState(
    duplicateFrom ? `${duplicateFrom.name} (copy)` : ""
  );
  const [authType, setAuthType] = useState<AuthenticationType>(
    duplicateFrom?.authenticationType ?? "certificate"
  );
  const [duration, setDuration] = useState(
    String(duplicateFrom?.durationDays ?? 365)
  );
  const [autoRenew, setAutoRenew] = useState(duplicateFrom?.autoRenew ?? true);
  const [keyType, setKeyType] = useState<KeyType>(
    duplicateFrom?.keyType ?? "rsa"
  );
  const [keyLength, setKeyLength] = useState(
    String(duplicateFrom?.keyLength ?? 2048)
  );
  const [hashType, setHashType] = useState<HashType>(
    duplicateFrom?.hashType ?? "sha256"
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | undefined>();

  const availableLengths = KEY_LENGTHS_BY_TYPE[keyType];

  const handleKeyTypeChange = (newType: KeyType) => {
    setKeyType(newType);
    const lengths = KEY_LENGTHS_BY_TYPE[newType];
    if (!lengths.includes(Number(keyLength))) {
      setKeyLength(String(lengths[0]));
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitError(undefined);
    const trimmedName = name.trim();
    if (trimmedName === "" || isSubmitting) return;

    const durationDays = Number.parseInt(duration.trim(), 10);
    if (!Number.isFinite(durationDays)) {
      setSubmitError("Duration must be a valid number of days.");
      return;
    }

    setIsSubmitting(true);
    try {
      await addCertificatePolicy({
        name: trimmedName,
        authenticationType: authType,
        durationDays,
        autoRenew,
        keyType,
        keyLength: Number(keyLength),
        hashType,
      });
      onClose();
    } catch (err) {
      setSubmitError(
        err instanceof Error
          ? err.message
          : "Failed to create certificate policy."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="vmx-modal">
      <div className="vmx-modal-header">
        <div className="vmx-modal-title">
          {duplicateFrom
            ? "Duplicate Certificate Policy"
            : "New Certificate Policy"}
        </div>
        <button
          className="vmx-btn vmx-btn-ghost vmx-btn-icon"
          onClick={onClose}
        >
          ✕
        </button>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="vmx-modal-body">
          <div className="vmx-form-group">
            <label className="vmx-label">Certificate Policy Name</label>
            <input
              className="vmx-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Production Certificate 90-day"
              required
              autoFocus
              disabled={isSubmitting}
            />
          </div>

          <div className="vmx-form-group">
            <label className="vmx-label">Authentication Type</label>
            <select
              className="vmx-select"
              value={authType}
              onChange={(e) =>
                setAuthType(e.target.value as AuthenticationType)
              }
              disabled={isSubmitting}
            >
              {AUTHENTICATION_TYPES.map((t) => (
                <option key={t} value={t} disabled={t !== "certificate"}>
                  {t === "certificate"
                    ? "Certificate"
                    : t === "jwt"
                      ? "JWT (coming soon)"
                      : "Symmetric Key (coming soon)"}
                </option>
              ))}
            </select>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 16,
            }}
          >
            <div className="vmx-form-group">
              <label className="vmx-label">Duration (days)</label>
              <input
                className="vmx-input"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                required
                disabled={isSubmitting}
              />
            </div>
            <div
              className="vmx-form-group"
              style={{ justifyContent: "flex-end" }}
            >
              <div className="vmx-checkbox-row" style={{ paddingBottom: 10 }}>
                <input
                  type="checkbox"
                  className="vmx-checkbox"
                  id="autoRenew"
                  checked={autoRenew}
                  onChange={(e) => setAutoRenew(e.target.checked)}
                  disabled={isSubmitting}
                />
                <label
                  htmlFor="autoRenew"
                  className="vmx-label"
                  style={{ cursor: "pointer" }}
                >
                  Auto Renew
                </label>
              </div>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 16,
            }}
          >
            <div className="vmx-form-group">
              <label className="vmx-label">Key Type</label>
              <select
                className="vmx-select"
                value={keyType}
                onChange={(e) => handleKeyTypeChange(e.target.value as KeyType)}
                disabled={isSubmitting}
              >
                {KEY_TYPES.map((t) => (
                  <option key={t} value={t} disabled={t === "aes"}>
                    {t === "aes" ? "AES (coming soon)" : t.toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
            <div className="vmx-form-group">
              <label className="vmx-label">Key Length</label>
              <select
                className="vmx-select"
                value={keyLength}
                onChange={(e) => setKeyLength(e.target.value)}
                disabled={isSubmitting}
              >
                {availableLengths.map((l) => (
                  <option key={l} value={l}>
                    {l}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="vmx-form-group">
            <label className="vmx-label">Hash Type</label>
            <select
              className="vmx-select"
              value={hashType}
              onChange={(e) => setHashType(e.target.value as HashType)}
              disabled={isSubmitting}
            >
              {HASH_TYPES.map((h) => (
                <option key={h} value={h} disabled={h !== "sha256"}>
                  {h !== "sha256"
                    ? `${h.toUpperCase()} (coming soon)`
                    : h.toUpperCase()}
                </option>
              ))}
            </select>
          </div>

          <div className="vmx-form-group">
            <label className="vmx-label">Signature Algorithm</label>
            <input
              className="vmx-input"
              value={getSignatureAlgorithm(keyType, hashType)}
              readOnly
              disabled
            />
          </div>

          {submitError !== undefined && (
            <div
              style={{
                marginTop: 4,
                fontSize: "0.8125rem",
                color: "var(--vmx-danger)",
              }}
            >
              {submitError}
            </div>
          )}
        </div>
        <div className="vmx-modal-footer">
          <button
            type="button"
            className="vmx-btn vmx-btn-secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="vmx-btn vmx-btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? duplicateFrom
                ? "Duplicating..."
                : "Creating..."
              : duplicateFrom
                ? "Duplicate"
                : "Create Policy"}
          </button>
        </div>
      </form>
    </div>
  );
};
